import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, filter, firstValueFrom } from 'rxjs';
import {
  AgentAvailabilityChangedDto,
  LiveTransferPositionInvalidatedDto,
  LiveTransferPositionSnapshotDto,
} from 'voxr-shared';
import { environment } from '../../environments/environment';
import { SocketService } from './socket.service';
import { UserService } from './user.service';
import { WorkspaceService } from './workspace.service';

const ACTIVE_REFRESH_INTERVAL = 60_000;
const IDLE_REFRESH_INTERVAL = 300_000;

@Injectable({ providedIn: 'root' })
export class LiveTransferPositionService {
  private http = inject(HttpClient);
  private socketService = inject(SocketService);
  private userService = inject(UserService);
  private workspaceService = inject(WorkspaceService);
  private destroyRef = inject(DestroyRef);
  private snapshotSubject = new BehaviorSubject<LiveTransferPositionSnapshotDto | undefined>(undefined);
  private workspaceId?: string;
  private refreshTimer?: ReturnType<typeof setInterval>;
  private refreshInterval?: number;
  private refreshDebounce?: ReturnType<typeof setTimeout>;
  private expiryTimer?: ReturnType<typeof setTimeout>;
  private requestId = 0;

  readonly snapshot$ = this.snapshotSubject.asObservable();

  constructor() {
    combineLatest([this.userService.user$, this.workspaceService.workspace$])
      .pipe(takeUntilDestroyed())
      .subscribe(([user, workspace]) => {
        const workspaceId = user && workspace?._id && !workspace._metadata?.coachOnly
          ? workspace._id.toString() : undefined;
        if (workspaceId === this.workspaceId) return;

        this.workspaceId = workspaceId;
        this.requestId++;
        this.snapshotSubject.next(undefined);
        this.resetTimers();
        if (!workspaceId) return;

        this.setRefreshInterval(ACTIVE_REFRESH_INTERVAL);
        void this.subscribeWorkspace();
        void this.refresh();
      });

    this.socketService.connected$
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => {
        void this.subscribeWorkspace();
        this.scheduleRefresh();
      });

    this.socketService.onEvent$<AgentAvailabilityChangedDto>('agent-availability-changed')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.scheduleRefresh());
    this.socketService.onEvent$<LiveTransferPositionInvalidatedDto>('live-transfer-position-invalidated')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.scheduleRefresh());
    for (const event of ['transfer-started', 'transfer-ended', 'campaign-task-refresh']) {
      this.socketService.onEvent$(event)
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.scheduleRefresh());
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') this.scheduleRefresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      this.resetTimers();
    });
  }

  private async subscribeWorkspace() {
    if (this.workspaceId
      && this.socketService.connected$.value
      && await this.workspaceService.hasAdminAccess()) {
      this.socketService.emit('subscribe-workspace', { workspaceId: this.workspaceId });
    }
  }

  private scheduleRefresh() {
    if (!this.workspaceId) return;
    clearTimeout(this.refreshDebounce);
    this.refreshDebounce = setTimeout(() => void this.refresh(), 1_000);
  }

  private setRefreshInterval(interval: number) {
    if (this.refreshTimer && this.refreshInterval === interval) return;
    clearInterval(this.refreshTimer);
    this.refreshInterval = interval;
    this.refreshTimer = setInterval(() => void this.refresh(), interval);
  }

  private async refresh() {
    const workspaceId = this.workspaceId;
    if (!workspaceId) return;

    const requestId = ++this.requestId;
    try {
      const snapshot = await firstValueFrom(this.http.get<LiveTransferPositionSnapshotDto>(
        `${environment.app.serviceUrl}/campaign/live-transfer-positions/self`
      ));
      if (requestId !== this.requestId || workspaceId !== this.workspaceId || snapshot.workspaceId !== workspaceId) return;

      this.setRefreshInterval(snapshot.trackedCampaignCount === 0 ? IDLE_REFRESH_INTERVAL : ACTIVE_REFRESH_INTERVAL);
      this.snapshotSubject.next(snapshot);
      clearTimeout(this.expiryTimer);
      const expiresIn = new Date(snapshot.expiresAt).getTime() - Date.now();
      this.expiryTimer = setTimeout(() => {
        if (this.snapshotSubject.value?.snapshotId === snapshot.snapshotId) {
          this.snapshotSubject.next(undefined);
        }
      }, Math.max(0, expiresIn));
    } catch {
      const expiresAt = this.snapshotSubject.value?.expiresAt;
      if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
        this.snapshotSubject.next(undefined);
      }
    }
  }

  private resetTimers() {
    clearInterval(this.refreshTimer);
    clearTimeout(this.refreshDebounce);
    clearTimeout(this.expiryTimer);
    this.refreshTimer = undefined;
    this.refreshInterval = undefined;
    this.refreshDebounce = undefined;
    this.expiryTimer = undefined;
  }
}
